import { useState, useEffect, useCallback } from 'react';
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter, ModalBody, ModalCloseButton,
    FormControl, FormLabel, Input, Select, Button, FormHelperText, useToast
} from '@chakra-ui/react';
import { authAPI } from '../services/api';
import MultiSelectClass from './MultiSelectClass';

const StaffFormModal = ({ isOpen, onClose, onSuccess, classes = [], institutions = [], user, isParentView = false, isAllView = false, editData = null }) => {
    const toast = useToast();
    const isEditMode = !!editData;
    const [formData, setFormData] = useState({
        full_name: '', phone_number: '', password: '',
        role: isParentView ? 'parent' : 'teacher',
        institution_id: '', teaching_class_ids: []
    });
    const [submitLoading, setSubmitLoading] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        if (editData) {
            let currentClassIds = [];
            if (editData.teaching_classes?.length > 0) currentClassIds = editData.teaching_classes.map(c => c.id);
            else if (editData.teaching_class?.id) currentClassIds = [editData.teaching_class.id];
            setFormData({
                full_name: editData.full_name,
                phone_number: editData.phone_number,
                password: '',
                role: editData.role,
                institution_id: editData.institution_id || '',
                teaching_class_ids: currentClassIds
            });
        } else {
            setFormData({ full_name: '', phone_number: '', password: '', role: isParentView ? 'parent' : 'teacher', institution_id: '', teaching_class_ids: [] });
        }
    }, [isOpen, editData, isParentView]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleClassChange = useCallback((newIds) => {
        setFormData(prev => ({ ...prev, teaching_class_ids: newIds }));
    }, []);

    const handleSubmit = async () => {
        if (!formData.full_name || !formData.phone_number) { toast({ title: '請填寫完整資訊', status: 'warning' }); return; }
        if (!isEditMode && !formData.password) { toast({ title: '請填寫密碼', status: 'warning' }); return; }
        const payload = {
            ...formData,
            institution_id: formData.institution_id ? parseInt(formData.institution_id) : undefined,
        };
        if (isEditMode && !payload.password) delete payload.password;
        setSubmitLoading(true);
        try {
            if (isEditMode) {
                await authAPI.updateUser(editData.id, payload);
                toast({ title: '資料更新成功', status: 'success', duration: 2000 });
            } else {
                await authAPI.createUser(payload);
                toast({ title: '人員新增成功', status: 'success' });
            }
            onSuccess();
            onClose();
        } catch (err) {
            toast({ title: isEditMode ? '更新失敗' : '新增失敗', description: err.response?.data?.detail || '請重新整理再試', status: 'error' });
        } finally {
            setSubmitLoading(false);
        }
    };

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered>
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>{isEditMode ? '編輯' : '新增'}{isParentView ? '家長' : '人員'}</ModalHeader>
                <ModalCloseButton />
                <ModalBody pb={6}>
                    <FormControl isRequired mb={4}>
                        <FormLabel>姓名</FormLabel>
                        <Input name="full_name" value={formData.full_name} onChange={handleInputChange} />
                    </FormControl>
                    <FormControl isRequired mb={4}>
                        <FormLabel>手機號碼 (帳號)</FormLabel>
                        <Input name="phone_number" value={formData.phone_number} onChange={handleInputChange} />
                    </FormControl>
                    <FormControl isRequired={!isEditMode} mb={4}>
                        <FormLabel>密碼</FormLabel>
                        <Input type="password" name="password" value={formData.password} onChange={handleInputChange}
                            placeholder={isEditMode ? '若不修改請留空' : '請設定密碼'} />
                        {isEditMode && <FormHelperText>留空則維持原密碼</FormHelperText>}
                    </FormControl>
                    {!isParentView && (
                        <>
                            <FormControl isRequired mb={4}>
                                <FormLabel>角色</FormLabel>
                                <Select name="role" value={formData.role} onChange={handleInputChange}>
                                    <option value="teacher">帶班老師</option>
                                    <option value="receptionist">行政老師</option>
                                    <option value="admin">機構管理員</option>
                                    {isAllView && <option value="parent">家長</option>}
                                </Select>
                            </FormControl>
                            {(formData.role === 'teacher' || formData.role === 'receptionist') && (
                                <FormControl mb={4}>
                                    <FormLabel>負責班級 (可多選)</FormLabel>
                                    <MultiSelectClass
                                        options={classes}
                                        value={formData.teaching_class_ids}
                                        onChange={handleClassChange}
                                        placeholder="選擇負責的班級"
                                    />
                                    <FormHelperText>若不指定，則視為未帶班 (行政或支援)</FormHelperText>
                                </FormControl>
                            )}
                        </>
                    )}
                    {user?.role === 'super_admin' && (
                        <FormControl mb={4}>
                            <FormLabel>所屬機構</FormLabel>
                            <Select name="institution_id" placeholder="請選擇機構" value={formData.institution_id} onChange={handleInputChange}>
                                {institutions.map(inst => <option key={inst.id} value={inst.id}>{inst.name}</option>)}
                            </Select>
                        </FormControl>
                    )}
                </ModalBody>
                <ModalFooter>
                    <Button onClick={onClose} mr={3}>取消</Button>
                    <Button colorScheme="brand" onClick={handleSubmit} isLoading={submitLoading}>
                        {isEditMode ? '儲存變更' : '確認新增'}
                    </Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default StaffFormModal;
